import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import EmptyState from "../../lib/components/EmptyState";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  lighten,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import transmissionService from "../../service/transmissionService";
import { useQuery } from "@tanstack/react-query";
import { formatOnboardingChannel, formatTransmissionMode } from "../../utils";
import moment from "moment";
import PreloaderTable from "../../lib/components/PreloaderTable";
import { theme } from "../../App";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import organizationservice from "../../service/organizationservice";
import { ArrowBack, Close, ContentCopy, Search } from "@mui/icons-material";
import useDebounce from "../../hooks/useDebounce";
import { FaClipboardList, FaCloudUploadAlt, FaFileInvoice, FaQrcode } from "react-icons/fa";
import { BiSolidWallet } from "react-icons/bi";
import pathnames from "../../constants/pathnames";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import scrollHandler from "../../utils/scrollHandler";
import AppTextField from "../../lib/components/AppTextField";
import AppSelectField from "../../lib/components/AppSelectField";
import { moneyFormat } from "../../utils/moneyFormat";
import { toast } from "react-toastify";
import DateRangePreset, { buildDateParams, DATE_PRESETS, extractFilterDates, normalizeDatePreset } from "./components/DateRangePreset";
import SortFilterMenu from "./components/SortFilterMenu";
import TrendPeriodFilter, {
  TREND_PERIODS,
  canFetchInvoiceTrends,
  parseWeekOptionsFromSeries,
  resolveTrendPeriod,
} from "./components/TrendPeriodFilter";

const TREND_COLORS = {
  completed: "#10B981",
  processing: "#F59E0B",
  failed: "#EF4444",
};

const FILE_REQUEST_SORT_BY_ITEMS = [
  { label: "Date", value: "createdAt" },
 
];

const ViewTransmissionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "6";
  const keyword = searchParams.get("keyword") || "";
  const status = searchParams.get("status") || "";
  const urlDatePreset = searchParams.get("datePreset");
  const urlDateFrom = searchParams.get("dateFrom") || null;
  const urlDateTo = searchParams.get("dateTo") || null;
  const sortBy = searchParams.get("sortBy") || "createdAt";
  const sortOrder = searchParams.get("sortOrder") || "desc";

  const searchTerm = useDebounce(keyword);
  const now = React.useMemo(() => moment(), []);
  const [trendYear, setTrendYear] = React.useState(now.year());
  const [trendMonth, setTrendMonth] = React.useState(now.month() + 1);
  const [trendWeek, setTrendWeek] = React.useState(null);
  /** Cached from WEEKLY series so the week dropdown stays available in DAILY drill-down. */
  const [trendWeekOptions, setTrendWeekOptions] = React.useState([]);
  const [invoiceCurrency, setInvoiceCurrency] = React.useState("NGN");
  const [responseFilters, setResponseFilters] = React.useState(null);

  const datePreset = normalizeDatePreset(urlDatePreset || responseFilters?.datePreset || DATE_PRESETS.THIS_MONTH);
  const dateFrom = urlDateFrom || responseFilters?.dateFrom || null;
  const dateTo = urlDateTo || responseFilters?.dateTo || null;

  // Match TransmissionPage: omit date params on first load so backend defaults to THIS_MONTH.
  const dateParams = urlDatePreset ? buildDateParams(urlDatePreset, urlDateFrom, urlDateTo) : {};

  const queries = {
    page,
    limit,
    id,
    keyword: searchTerm,
    status,
    sortBy,
    sortOrder,
    ...dateParams,
  };

  const trendPeriod = resolveTrendPeriod({ year: trendYear, month: trendMonth, week: trendWeek });
  const trendOpts = { year: trendYear, month: trendMonth, week: trendWeek };
  const trendsEnabled = Boolean(id) && canFetchInvoiceTrends(trendPeriod, trendOpts);

  const organizationQuery = useQuery({
    retry: false,
    queryKey: ["organizations-details", id],
    queryFn: () => organizationservice.getOne({ id }),
  });

  const firsIntegrationQuery = useQuery({
    enabled: Boolean(id),
    retry: false,
    queryKey: ["firs-integrations", id],
    queryFn: () => organizationservice.getFirsIntegration({ organizationId: id }),
  });

  const fileRequestsQuery = useQuery({
    retry: false,
    queryKey: [`file-requests-${id}`, ...Object.values(queries)],
    queryFn: () => organizationservice.fileRequests({ ...queries }),
  });

  const fileRequests = fileRequestsQuery?.data?.data;
  /** Summary counts now come from file-requests (transmission-count-summary is deprecated). */
  const transmissionCount = fileRequests?.summary;
  const isSummaryLoading = fileRequestsQuery.isPending;

  const organization = organizationQuery?.data?.data?.data;
  const firsIntegration = firsIntegrationQuery?.data?.data?.data?.[0];
  const isOrgLoading = organizationQuery.isPending;

  const transmissionTrendsQuery = useQuery({
    retry: false,
    enabled: trendsEnabled,
    queryKey: ["transmission-trends", id, trendPeriod, trendYear, trendMonth, trendWeek],
    queryFn: () =>
      transmissionService.trends({
        organizationId: id,
        period: trendPeriod,
        year: trendYear,
        month: trendMonth,
        week: trendWeek,
      }),
  });

  // Re-sync date UI from backend filters (same flow as TransmissionPage).
  React.useEffect(() => {
    const filters = fileRequests?.filters;
    if (!filters) return;
    setResponseFilters(extractFilterDates(filters));
  }, [fileRequests?.filters]);

  const trendsPayload = transmissionTrendsQuery.data?.data?.data;
  const rawTrends = Array.isArray(trendsPayload) ? trendsPayload : trendsPayload?.series ?? [];

  // Week filter options come from BE WEEKLY series labels (W1, W2, …).
  React.useEffect(() => {
    if (trendPeriod !== TREND_PERIODS.WEEKLY) return;
    const series = Array.isArray(trendsPayload) ? trendsPayload : trendsPayload?.series;
    if (!Array.isArray(series) || !series.length) return;
    setTrendWeekOptions(parseWeekOptionsFromSeries(series));
  }, [trendPeriod, trendsPayload]);

  const formattedTransmissionTrendsData = rawTrends.map((d) => {
    const completed = Number(d.successful ?? d.completed ?? d.success ?? 0);
    const processing = Number(d.pending ?? d.processing ?? 0);
    const failed = Number(d.failed ?? 0);
    const hasBreakdown = completed > 0 || processing > 0 || failed > 0;

    return {
      label: d.label,
      completed: hasBreakdown ? completed : Number(d.count ?? d.value ?? 0),
      processing: hasBreakdown ? processing : 0,
      failed: hasBreakdown ? failed : 0,
    };
  });

  function handleTrendYearChange(nextYear) {
    setTrendYear(nextYear);
    setTrendMonth(null);
    setTrendWeek(null);
    setTrendWeekOptions([]);
  }

  function handleTrendMonthChange(nextMonth) {
    setTrendMonth(nextMonth);
    setTrendWeek(null);
    setTrendWeekOptions([]);
  }

  function handleTrendWeekChange(nextWeek) {
    setTrendWeek(nextWeek);
  }

  const invoiceValueRaw = transmissionCount?.totalInvoiceValue;
  const currencyOptions = React.useMemo(() => getInvoiceCurrencyOptions(invoiceValueRaw), [invoiceValueRaw]);

  React.useEffect(() => {
    if (!currencyOptions.includes(invoiceCurrency)) {
      setInvoiceCurrency(currencyOptions[0] || "NGN");
    }
  }, [currencyOptions, invoiceCurrency]);

  const invoiceDisplayValue = formatInvoiceCurrencyValue(invoiceValueRaw, invoiceCurrency);

  const statsCards = [
    { label: "Total Transmissions", value: transmissionCount?.totalTransmissions ?? 0 },
    { label: "Successful Transmissions", value: transmissionCount?.successful ?? 0 },
    { label: "Processing Transmissions", value: transmissionCount?.pending ?? 0 },
    { label: "Failed Transmissions", value: transmissionCount?.failed ?? 0 },
  ];

  function updateSearchParams(mutator) {
    const next = new URLSearchParams(window.location.search);
    mutator(next);
    navigate(
      {
        pathname: window.location.pathname,
        search: next.toString(),
      },
      { replace: true },
    );
  }

  function handleChange(e, name = "") {
    const rawValue = name === "page" || name === "limit" ? e : e?.target?.value;
    // Guard against accidentally passing a DOM/event object (e.g. `[object PointerEvent]`) so
    // invalid values never leak into the URL (e.g. `?limit=%5Bobject+PointerEvent%5D`).
    const value =
      rawValue && typeof rawValue === "object" && rawValue?.target?.value != null
        ? rawValue.target.value
        : rawValue;
    updateSearchParams((params) => {
      if (value === "" || value == null) params.delete(name);
      else params.set(name, value);
      if (name !== "page") params.set("page", "1");
    });
    if (name === "page") scrollHandler();
  }

  function handlePresetChange(preset) {
    updateSearchParams((params) => {
      params.set("datePreset", preset);
      params.set("page", "1");
      if (preset !== DATE_PRESETS.CUSTOM) {
        params.delete("dateFrom");
        params.delete("dateTo");
      }
    });
  }

  function handleCopy(value, label) {
    if (!value) return;
    navigator.clipboard.writeText(String(value)).then(
      () => toast.success(`${label} copied`),
      () => toast.error(`Could not copy ${label.toLowerCase()}`),
    );
  }

  function handleCustomRangeChange({ dateFrom: from, dateTo: to }) {
    updateSearchParams((params) => {
      params.set("datePreset", DATE_PRESETS.CUSTOM);
      params.set("dateFrom", from);
      params.set("dateTo", to);
      params.set("page", "1");
    });
  }

  return (
    <AppLayout>
      <Header title="FIRS Transmissions" description="Get insights to Firs Transmissions on here" />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2} mt={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton className="bg-secondary-subtle" onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
          </Box>

          <Box
            display="inline-flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: "pointer",
              color: "#2563EB",
            }}
            onClick={() => navigate(`${pathnames.API_TRANSMISSIONS}/${id}`)}
          >
            <Typography fontSize={14} fontWeight={500}>
              Switch Dashboard
            </Typography>

            <CompareArrowsIcon sx={{ fontSize: 24 }} />
          </Box>
        </Box>

        <Grid container spacing={2} mb={2} alignItems="stretch">
          <Grid item xs={12} md={2.88} sx={{ minWidth: 0, maxWidth: "100%" }}>
            <Paper sx={{ p: 2.5, borderRadius: 1, height: "100%" }}>
              {isOrgLoading ? (
                <Box>
                  <Skeleton width="60%" height={28} sx={{ mb: 2 }} />
                  <Skeleton width="80%" height={20} sx={{ mb: 1 }} />
                  <Skeleton width="75%" height={20} sx={{ mb: 1 }} />
                  <Skeleton width="50%" height={20} sx={{ mb: 1 }} />
                  <Skeleton width="45%" height={20} sx={{ mb: 2 }} />
                  <Divider sx={{ my: 2 }} />
                  <Skeleton width="70%" height={20} sx={{ mb: 1 }} />
                  <Skeleton width="65%" height={20} sx={{ mb: 1 }} />
                  <Skeleton width="55%" height={20} />
                </Box>
              ) : (
                <>
                  <Typography fontSize={18} fontWeight={600} mb={2}>
                    {organization?.name || "—"}
                  </Typography>

                  <OrgDetailRow
                    label="TIN:"
                    value={organization?.firstin || "N/A"}
                    onCopy={organization?.firstin ? () => handleCopy(organization.firstin, "TIN") : undefined}
                  />
                  <OrgDetailRow
                    label="Email:"
                    value={organization?.email || "N/A"}
                    href={organization?.email ? `mailto:${organization.email}` : undefined}
                    onCopy={organization?.email ? () => handleCopy(organization.email, "Email") : undefined}
                  />
                  <OrgDetailRow
                    label="Join date:"
                    value={organization?.createdAt ? moment(organization.createdAt).format("M/D/YYYY") : "N/A"}
                  />
                  <OrgDetailRow label="ERP:" value={firsIntegration?.data?.erp || "N/A"} />

                  <Divider sx={{ my: 2 }} />

                  <OrgDetailRow
                    label="Subscription Period:"
                    value={formatBillingInterval(organization?.subscription?.billingInterval)}
                  />
                  <OrgDetailRow
                    label="Next Due date:"
                    value={organization?.subscription?.endAt ? moment(organization.subscription.endAt).format("M/D/YYYY") : "N/A"}
                  />
                  <OrgDetailRow
                    label="Recurrent Cost:"
                    value={
                      organization?.subscription?.amount != null
                        ? moneyFormat(organization.subscription.amount, "NGN")
                        : "N/A"
                    }
                  />
                  <OrgDetailRow
                    label="Integration Method:"
                    value={
                      formatTransmissionMode(organization?.firs?.transmissionMode ?? firsIntegration?.data?.transmissionMode) ?? "N/A"
                    }
                  />
                  <OrgDetailRow label="Onboarding Channel:" value={formatOnboardingChannel(organization?.firs?.onboardingChannel)} />
                </>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={9.12} sx={{ minWidth: 0, maxWidth: "100%" }}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 1,
                height: "100%",
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              <Box mb={2} sx={{ minWidth: 0, maxWidth: "100%" }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                  sx={{ minWidth: 0, maxWidth: "100%" }}
                >
                  <Typography fontSize={16} fontWeight={600} sx={{ flexShrink: 0 }}>
                    Transmission Trends
                  </Typography>
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    gap={2}
                    flexWrap="wrap"
                    sx={{ flexShrink: 0, maxWidth: "100%", ml: "auto" }}
                  >
                    <Box display="inline-flex" alignItems="center" gap={1.5} sx={{ flexShrink: 0 }}>
                      <TrendLegendSwatch color={TREND_COLORS.completed} label="Completed" />
                      <TrendLegendSwatch color={TREND_COLORS.processing} label="Processing" />
                      <TrendLegendSwatch color={TREND_COLORS.failed} label="Failed" />
                    </Box>
                    <TrendPeriodFilter
                      year={trendYear}
                      month={trendMonth}
                      week={trendWeek}
                      weekOptions={trendWeekOptions}
                      onYearChange={handleTrendYearChange}
                      onMonthChange={handleTrendMonthChange}
                      onWeekChange={handleTrendWeekChange}
                    />
                  </Box>
                </Box>
              
              </Box>

              {transmissionTrendsQuery.isLoading || transmissionTrendsQuery.isFetching ? (
                <Skeleton variant="rounded" height={280} />
              ) : formattedTransmissionTrendsData.length === 0 ? (
                <EmptyState title="No trend data for this period" />
              ) : (
                <Box sx={{ width: "100%", maxWidth: "100%", height: 280, minWidth: 0, overflow: "hidden" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={formattedTransmissionTrendsData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" interval={0} tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend content={() => null} />
                      <Bar dataKey="completed" name="Completed" stackId="trends" fill={TREND_COLORS.completed} barSize={trendPeriod === TREND_PERIODS.DAILY ? 12 : 28} />
                      <Bar dataKey="processing" name="Processing" stackId="trends" fill={TREND_COLORS.processing} barSize={trendPeriod === TREND_PERIODS.DAILY ? 12 : 28} />
                      <Bar dataKey="failed" name="Failed" stackId="trends" fill={TREND_COLORS.failed} barSize={trendPeriod === TREND_PERIODS.DAILY ? 12 : 28} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: "#9CA3AF", borderBottomWidth: 2 }} />

        <Grid container spacing={2} mb={2}>
          {statsCards.map((card) => (
            <Grid item xs={12} lg={3} key={card.label}>
              <Paper sx={{ p: 2, borderRadius: 1, height: "100%" }}>
                <Typography mb={2} color="text.secondary">
                  {card.label}
                </Typography>
                {isSummaryLoading ? (
                  <Skeleton variant="text" width="50%" height={28} />
                ) : (
                  <Typography fontSize={24} fontWeight={500} sx={{ wordBreak: "break-word" }}>
                    {Number(card.value).toLocaleString()}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}

          <Grid item xs={12} lg={3}>
            <Paper sx={{ p: 2, borderRadius: 1, backgroundColor: "#007bff", color: "#fff", height: "100%" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} gap={1}>
                <Typography>
                  Total Invoice Value
                </Typography>
                <Select
                  value={invoiceCurrency}
                  onChange={(e) => setInvoiceCurrency(e.target.value)}
                  size="small"
                  variant="standard"
                  disableUnderline
                  sx={{
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 500,
                    "& .MuiSelect-icon": { color: "#fff" },
                    "& .MuiSelect-select": { py: 0, pr: "20px !important" },
                  }}
                >
                  {currencyOptions.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {isSummaryLoading ? (
                <Skeleton variant="text" width="70%" height={28} sx={{ bgcolor: "rgba(255,255,255,0.35)" }} />
              ) : (
                <Typography fontSize={24} fontWeight={500} sx={{ wordBreak: "break-word" }}>
                  {invoiceDisplayValue}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Typography fontSize={20} fontWeight={700} mb={2}>
          Transmissions
        </Typography>

        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2} flexWrap="wrap">
          <Box flexGrow={1} sx={{ maxWidth: 480, minWidth: 200 }}>
            <AppTextField
              placeholder="Search transmissions..."
              value={keyword}
              onChange={(e) => handleChange(e, "keyword")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search className="text-secondary" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <SortFilterMenu
              sortBy={sortBy}
              sortOrder={sortOrder}
              sortByItems={FILE_REQUEST_SORT_BY_ITEMS}
              onSortByChange={(e) => handleChange(e, "sortBy")}
              onSortOrderChange={(e) => handleChange(e, "sortOrder")}
            />
            <DateRangePreset
              datePreset={datePreset}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onPresetChange={handlePresetChange}
              onCustomRangeChange={handleCustomRangeChange}
            />
          </Box>
        </Box>

        <Paper sx={{ p: 2, borderRadius: 1 }}>
          {fileRequestsQuery.isPending ? null : (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: "#F6F6F6" }}>
                  <TableRow>
                    <TableCell sx={{ py: 1 }}>S/N</TableCell>
                    <TableCell sx={{ py: 1, maxWidth: 300 }}>File</TableCell>
                    <TableCell sx={{ py: 1 }}>Date</TableCell>
                    <TableCell sx={{ py: 1 }}>Status</TableCell>
                    <TableCell sx={{ py: 1 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fileRequests?.data?.map((item, index) => (
                    <TableRow key={item?.id}>
                      <TableCell sx={{ py: 1 }}>{(Math.max(1, parseInt(page, 10) || 1) - 1) * Math.max(1, parseInt(limit, 10) || 20) + index + 1}</TableCell>
                      <TableCell sx={{ py: 1, maxWidth: 300 }}>{item?.file?.originalName}</TableCell>
                      <TableCell sx={{ py: 1 }}>{moment(item?.createdAt).format("DD-MM-YYYY LT")}</TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <TransmissionStatus status={item?.status} />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Box component="span" display="flex" alignItems="center" gap={1}>
                          <Typography
                            component={Link}
                            to={`${pathnames.TRANSMITTED_INVOICES}/${item?.id}?organizationId=${id}`}
                            color={theme.palette.secondary.main}
                            className="click"
                          >
                            View
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <EmptyState isEmpty={!fileRequestsQuery.isPending && !fileRequests?.data?.length} />
        </Paper>

        <PreloaderTable isLoading={fileRequestsQuery.isPending} />
        <div className="my-3">
          <div className="d-flex align-items-center gap-4 justify-content-between">
            <div>
              <AppSelectField
                value={limit}
                placeholder="Limit"
                onChange={(e) => handleChange(e.target.value, "limit")}
                items={[
                  { label: "6", value: "6" },
                  { label: "10", value: "10" },
                  { label: "20", value: "20" },
                  { label: "50", value: "50" },
                ]}
              />
            </div>
            <div>
              <Pagination
                page={Math.max(1, parseInt(page, 10) || 1)}
                count={fileRequests?.totalPages || 1}
                onChange={(_, value) => handleChange(value, "page")}
                shape="rounded"
                color="primary"
              />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

function TrendLegendSwatch({ color, label }) {
  return (
    <Box display="flex" alignItems="center" gap={0.75}>
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
      <Typography fontSize={13} color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function OrgDetailRow({ label, value, href, onCopy }) {
  return (
    <Box display="flex" alignItems="center" gap={1} mb={1.25} minWidth={0}>
      <Typography fontSize={13} color="text.secondary" component="span" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Box display="inline-flex" alignItems="center" gap={0.5} minWidth={0} sx={{ flexWrap: "nowrap" }}>
        {href ? (
          <Typography
            component="a"
            href={href}
            fontSize={13}
            fontWeight={500}
            sx={{
              color: "inherit",
              textDecoration: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {value}
          </Typography>
        ) : (
          <Typography
            fontSize={13}
            fontWeight={500}
            component="span"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
          >
            {value}
          </Typography>
        )}
        {onCopy ? (
          <IconButton size="small" onClick={onCopy} aria-label="Copy" sx={{ p: 0.25, color: "#6B7280", flexShrink: 0 }}>
            <ContentCopy sx={{ fontSize: 14 }} />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
}

function formatBillingInterval(interval) {
  if (!interval) return "N/A";
  return String(interval).charAt(0).toUpperCase() + String(interval).slice(1).toLowerCase();
}

const INVOICE_META_KEYS = new Set(["total", "submitted", "pending", "failed", "successful", "count"]);

/** Real currency codes only — never treat meta fields like `submitted` as currencies. */
function getInvoiceCurrencyOptions(value) {
  if (!value || typeof value !== "object") return ["NGN"];
  const codes = Object.keys(value).filter((key) => {
    if (INVOICE_META_KEYS.has(key)) return false;
    if (!/^[A-Z]{3}$/i.test(key)) return false;
    return Number.isFinite(Number(value[key]));
  });
  return codes.length ? codes.map((c) => c.toUpperCase()) : ["NGN"];
}

/**
 * Prefer `totalInvoiceValue.total` (not `submitted` or other meta fields).
 * Falls back to a selected currency amount only when `total` is absent.
 */
function formatInvoiceCurrencyValue(value, currency = "NGN") {
  if (value == null || value === "") return "₦0.00";

  let amount = 0;
  if (typeof value === "number" || typeof value === "string") {
    amount = Number(value);
  } else if (typeof value === "object") {
    if (value.total != null) {
      amount = Number(value.total);
    } else {
      const currencyAmount = value[currency] ?? value.NGN;
      amount = Number(currencyAmount ?? 0);
    }
  }

  if (Number.isNaN(amount)) amount = 0;
  const symbol = currency === "NGN" ? "₦" : `${currency} `;
  return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function ViewTimelineModal({ isOpen, onClose, invoice }) {
  return (
    <Dialog onClose={onClose} open={isOpen} maxWidth="xs" PaperProps={{ className: "w-100" }}>
      <div className="d-flex justify-content-between align-items-center gap-4 p-3 py-2 pe-2">
        <div className="fs-5 fw-medium">Transmission Timeline</div>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </div>
      <Divider className="bg-secondary-subtle" />
      <DialogContent className="mb-5">
        {invoice?.billedTo?.type === "BUSINESS" ? (
          <React.Fragment>
            {invoice?.type === "INVOICE" ? (
              <React.Fragment>
                <InvoiceStep
                  name={"SIGN_EXT_FIRS_INVOICE"}
                  invoice={invoice}
                  title={
                    invoice?.type === "INVOICE" ? "Sign Invoice" : invoice?.type === "CREDIT_NOTE" ? "Sign Credit Note" : "Sign Debit Note"
                  }
                  Icon={FaClipboardList}
                />

                <TimelineDivider />
                <InvoiceStep title={"Generate QR Code"} name={"GENERATE_EXT_FIRS_INVOICE_QR_CODE"} invoice={invoice} Icon={FaQrcode} />
                <TimelineDivider />

                <InvoiceStep
                  title={"Transmission Initiated"}
                  name={"TRANSMIT_EXT_FIRS_INVOICE"}
                  invoice={invoice}
                  Icon={FaCloudUploadAlt}
                />
                <TimelineDivider />
                <InvoiceStep
                  title={"Transmission Completed"}
                  name={"TRANSMIT_EXT_FIRS_INVOICE_COMPLETED"}
                  invoice={invoice}
                  Icon={FaCloudUploadAlt}
                />
                <TimelineDivider />

                <InvoiceStep
                  title={"Invoice Payment Status"}
                  name={"UPDATE_EXT_FIRS_INVOICE_PAYMENT_STATUS"}
                  invoice={invoice}
                  Icon={FaFileInvoice}
                />
                <TimelineDivider />
                <InvoiceStep
                  title={"Invoice Payment Reporting"}
                  name={"REPORT_EXT_FIRS_INVOICE_PAYMENT"}
                  invoice={invoice}
                  Icon={BiSolidWallet}
                />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <InvoiceStep
                  name={"SIGN_EXT_FIRS_INVOICE"}
                  invoice={invoice}
                  title={invoice?.type === "CREDIT_NOTE" ? "Sign Credit Note" : "Sign Debit Note"}
                  Icon={FaClipboardList}
                />
                <TimelineDivider />
                <InvoiceStep
                  title={"Transmission Initiated"}
                  name={"TRANSMIT_EXT_FIRS_INVOICE"}
                  invoice={invoice}
                  Icon={FaCloudUploadAlt}
                />
                <TimelineDivider />
                <InvoiceStep
                  title={"Transmission Completed"}
                  name={"TRANSMIT_EXT_FIRS_INVOICE_COMPLETED"}
                  invoice={invoice}
                  Icon={FaCloudUploadAlt}
                />
              </React.Fragment>
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {invoice?.type === "INVOICE" ? (
              <React.Fragment>
                <InvoiceStep
                  name={"SIGN_EXT_FIRS_INVOICE"}
                  invoice={invoice}
                  title={
                    invoice?.type === "INVOICE" ? "Sign Invoice" : invoice?.type === "CREDIT_NOTE" ? "Sign Credit Note" : "Sign Debit Note"
                  }
                  Icon={FaClipboardList}
                />

                <TimelineDivider />
                <InvoiceStep title={"Generate QR Code"} name={"GENERATE_EXT_FIRS_INVOICE_QR_CODE"} invoice={invoice} Icon={FaQrcode} />
                <TimelineDivider />
                <InvoiceStep
                  title={"Invoice Payment Status"}
                  name={"UPDATE_EXT_FIRS_INVOICE_PAYMENT_STATUS"}
                  invoice={invoice}
                  Icon={FaFileInvoice}
                />
                <TimelineDivider />
                <InvoiceStep
                  title={"Invoice Payment Reporting"}
                  name={"REPORT_EXT_FIRS_INVOICE_PAYMENT"}
                  invoice={invoice}
                  Icon={BiSolidWallet}
                />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <InvoiceStep
                  name={"SIGN_EXT_FIRS_INVOICE"}
                  invoice={invoice}
                  title={invoice?.type === "CREDIT_NOTE" ? "Sign Credit Note" : "Sign Debit Note"}
                  Icon={FaClipboardList}
                />
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceStep({ name, invoice, title, Icon }) {
  const action = invoice?.integration?.firs?.actions?.find((item) => item?.name === name);
  if (action?.status === "SKIPPED") {
    return null;
  }
  return (
    <Box display="flex" alignItems="center" gap={3}>
      <Box display="flex" width={56} height={48} alignItems="center" justifyContent="center" borderRadius={1} bgcolor="#000000">
        <Icon size={20} color="#ffffff" />
      </Box>
      <div>
        <Typography fontWeight={500}>{title}</Typography>
        <Typography
          fontSize={13}
          className={`${action?.status === "FAILED" ? "text-danger" : action?.status === "SUCCESS" ? "text-success" : "text-secondary"}`}
        >
          {action?.status === "FAILED" ? (
            <Typography>{action?.error} </Typography>
          ) : action?.status === "SUCCESS" ? (
            "Process completed"
          ) : (
            <Typography fontSize={13} component="span" className="text-warning">
              Pending
            </Typography>
          )}
        </Typography>
        <Typography fontSize={12} className="text-secondary">
          {action?.syncInitiatedAt ? moment(action?.syncInitiatedAt).format("DD-MM-YYYY LT") : null}
        </Typography>
      </div>
    </Box>
  );
}

export function TimelineDivider() {
  return (
    <Box sx={{ width: 56, height: 48, display: "flex", justifyContent: "center" }}>
      <Divider orientation="vertical" flexItem style={{ height: 100 }} />
    </Box>
  );
}

function TransmissionStatus({ status }) {
  if (status === "FAILED") {
    return (
      <Box
        bgcolor={lighten(theme.palette.error.main, 0.8)}
        color={theme.palette.error.main}
        px={3}
        py={1}
        borderRadius={20}
        textAlign="center"
        width="max-content"
      >
        Failed
      </Box>
    );
  }
  if (status === "PENDING") {
    return (
      <Box
        bgcolor={lighten(theme.palette.warning.main, 0.8)}
        color={theme.palette.warning.main}
        px={3}
        py={1}
        borderRadius={20}
        textAlign="center"
        width="max-content"
      >
        Pending
      </Box>
    );
  }
  if (status === "PROCESSING") {
    return (
      <Box
        bgcolor={lighten(theme.palette.success.main, 0.8)}
        color={theme.palette.success.main}
        px={3}
        py={1}
        borderRadius={20}
        textAlign="center"
        width="max-content"
      >
        Processing
      </Box>
    );
  }
  if (status === "TRANSMISSION_READY") {
    return (
      <Box
        bgcolor={lighten(theme.palette.primary.main, 0.8)}
        color={theme.palette.primary.main}
        px={3}
        py={1}
        borderRadius={20}
        textAlign="center"
        width="max-content"
      >
        Completed
      </Box>
    );
  }
  if (status === "TRANSMISSION_INITIATED") {
    return (
      <Box
        bgcolor={lighten(theme.palette.info.main, 0.8)}
        color={theme.palette.info.main}
        px={3}
        py={1}
        borderRadius={20}
        textAlign="center"
        width="max-content"
      >
        Transmitted
      </Box>
    );
  }

  if (status === "TRANSMITTED") {
    return (
      <Box
        bgcolor={lighten(theme.palette.success.main, 0.8)}
        color={theme.palette.success.main}
        px={3}
        py={1}
        borderRadius={20}
        textAlign="center"
        width="max-content"
      >
        Transmitted
      </Box>
    );
  }
  return null;
}

export default ViewTransmissionPage;
